// How loud the game is, as a setting the player keeps.
//
// The `cameraPrefs` pattern — one JSON key, a `Partial<T>` parse, a patch
// setter — with one difference that is the whole of why this file needs a test:
// **five of the six fields are numbers**, where that bag is almost all booleans.
// Absent is the only wrong shape a boolean has; a volume off `localStorage` can
// also arrive as `"0.8"` from a hand-edited store, as `3` from a shape that has
// since changed, or as `NaN` — and a `NaN` does not fall back when it reaches an
// `AudioParam`, it poisons the node and silences everything hanging off it. So
// every number goes through `clampVolume`, never through `??`.
//
// **Mute is a separate field rather than a master of nought**, so unmuting is
// the same call with the same number and a mute button cannot lose somebody's
// setting. The engine is what turns it into a gain.

import { useCallback, useState } from "react";

import { clampVolume } from "src/client/ai/audio/mix";

export interface SoundPrefs {
  /** Everything, at once. What mute switches off. */
  master: number;
  music: number;
  ambience: number;
  /** The board: pieces, machines, belts. */
  sfx: number;
  /** Chrome: buttons, panels, refusals. */
  ui: number;
  muted: boolean;
}

/**
 * Loud enough to be heard, quiet enough not to be the first thing anybody
 * turns down.
 *
 * Not 1 anywhere: a game that opens at full on every bus is one whose first
 * gesture is reaching for the slider. Sound on by default, because a sound
 * system nobody discovers is one nobody has. The board (`sfx`) and the master
 * sit above the beds, since what a player wants to hear is what they did.
 */
export const DEFAULT_SOUND_PREFS: SoundPrefs = {
  master: 0.8,
  music: 0.7,
  ambience: 0.7,
  sfx: 0.8,
  ui: 0.7,
  muted: false
};

/**
 * Load-bearing once shipped — never rename it.
 *
 * A renamed key is not a migration, it is every player's settings silently
 * reset to the defaults, which reads as the game having forgotten them.
 */
const KEY = "chessfactory.sound";

/**
 * The stored settings, or the defaults on anything at all.
 *
 * Anything: no storage (a private window refuses it and *throws* on access),
 * a value from an older shape, a half-written string. Volume is not worth an
 * exception on first paint, and this runs before the board is up.
 */
export function loadSoundPrefs(): SoundPrefs {
  try {
    const raw = window.localStorage?.getItem(KEY);
    if (!raw) return DEFAULT_SOUND_PREFS;
    const parsed = JSON.parse(raw) as Partial<SoundPrefs>;
    return {
      // `clampVolume` rather than `??` on every one of these: a `??` waves
      // through a string, a `NaN` and a 3 alike, and each of those is a gain
      // node that either does nothing sensible or stops working entirely.
      master: clampVolume(parsed.master, DEFAULT_SOUND_PREFS.master),
      music: clampVolume(parsed.music, DEFAULT_SOUND_PREFS.music),
      ambience: clampVolume(parsed.ambience, DEFAULT_SOUND_PREFS.ambience),
      sfx: clampVolume(parsed.sfx, DEFAULT_SOUND_PREFS.sfx),
      ui: clampVolume(parsed.ui, DEFAULT_SOUND_PREFS.ui),
      // The one boolean, so the one `??`: absent is the only wrong shape it
      // has, and `undefined` reaching the engine is not the same as `false`.
      muted: parsed.muted ?? DEFAULT_SOUND_PREFS.muted
    };
  } catch {
    return DEFAULT_SOUND_PREFS;
  }
}

export function saveSoundPrefs(prefs: SoundPrefs): void {
  try {
    window.localStorage?.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // A setting that cannot be remembered still has to work for this session.
  }
}

/**
 * The volume settings, held in state and kept across games.
 *
 * `set` takes a patch rather than a whole object, so a caller moving one
 * slider cannot silently reset the other five — which is exactly the bug a
 * panel of six controls invites.
 */
export function useSoundPrefs(): [SoundPrefs, (patch: Partial<SoundPrefs>) => void] {
  const [prefs, setPrefs] = useState<SoundPrefs>(loadSoundPrefs);
  const set = useCallback((patch: Partial<SoundPrefs>) => {
    setPrefs((was) => {
      const next = { ...was, ...patch };
      saveSoundPrefs(next);
      return next;
    });
  }, []);
  return [prefs, set];
}
